/**
 * The event queue: many events, few requests. Carried over from ugio_klase's
 * `child/events.ts` (buffer, send on moments not timers, one send at a time,
 * a failed batch back to the front) with events.md §8.4's three changes:
 *
 * 1. **Mirrored to storage**, keyed by the sitting, and replayed on the next
 *    load, so a closed lid or a crashed tab loses nothing. The ids make a
 *    double send harmless.
 * 2. **`pagehide` sends the buffer with `sendBeacon`**, and still keeps it in
 *    storage until a later receipt clears it.
 * 3. **Direct POSTs** (an answer, a skip, a request) are not queued: the
 *    caller flushes first, so her beat and rung events precede the answer in
 *    `seq`, then posts alone.
 *
 * The moments that flush: 8 waiting, an item or test ending, the tab hidden,
 * before a direct POST, and right after a type his grid shows
 * (`explanation.closed`, `session.*`).
 *
 * Only a network error, a 5xx or a 409 (no sitting yet: the picker is up)
 * counts as a failure. A 2xx with per-event receipts clears every event it
 * names, rejected ones included; a 4xx other than 409 drops the batch, since
 * sending it again would be refused again.
 */
import type { ClientEvent, Receipt } from "./api";
import { ApiError } from "../lib/http";

export interface KV {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export const browserKV: KV = {
  get: (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* storage full or blocked: the queue still works in memory */
    }
  },
  remove: (k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

export const BATCH = 8;
export const GRID_VISIBLE = /^(explanation\.(opened|closed)|session\.)/;

export interface EventQueue {
  push(event: ClientEvent): void;
  flush(): Promise<void>;
  pending(): number;
  /** `pagehide`: one beacon with everything unacknowledged. */
  beacon(): void;
}

export interface QueueOptions {
  kv?: KV;
  /** The storage key: the sitting the events belong to. */
  key: string;
  batchSize?: number;
  beacon?: (events: ClientEvent[]) => boolean;
  onFailure?: (error: unknown) => void;
}

function isFailure(e: unknown): boolean {
  if (e instanceof ApiError) return e.status >= 500 || e.status === 409;
  return true; // a network error, or anything that is not an HTTP answer
}

/** What a queue under `key` left unacknowledged in storage, oldest first. A
 *  screen that rebuilds her state from the server folds these over it, so a
 *  reload while offline still shows what she typed. */
export function storedEvents(key: string, kv: KV = browserKV): ClientEvent[] {
  try {
    const raw = kv.get(`ugis.queue.${key}`);
    const parsed = raw ? (JSON.parse(raw) as ClientEvent[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function createEventQueue(
  send: (events: ClientEvent[]) => Promise<{ receipts: Receipt[] }>,
  opts: QueueOptions,
): EventQueue {
  const kv = opts.kv ?? browserKV;
  const batchSize = Math.max(1, opts.batchSize ?? BATCH);
  const storeKey = `ugis.queue.${opts.key}`;
  // Replayed from the last load of this sitting: failed batches first.
  let buffer: ClientEvent[] = storedEvents(opts.key, kv);
  /** Unacknowledged: the buffer plus whatever is on the wire. */
  let inFlight: ClientEvent[] = [];
  let chain: Promise<void> = Promise.resolve();

  const persist = () => {
    const all = [...inFlight, ...buffer];
    if (all.length) kv.set(storeKey, JSON.stringify(all));
    else kv.remove(storeKey);
  };

  const drain = async (): Promise<void> => {
    if (buffer.length === 0) return;
    const batch = buffer.slice(0, 64); // the endpoint takes 64 at most
    buffer = buffer.slice(batch.length);
    inFlight = batch;
    try {
      await send(batch);
      inFlight = [];
      persist();
    } catch (error) {
      inFlight = [];
      if (isFailure(error)) buffer = [...batch, ...buffer];
      persist();
      opts.onFailure?.(error);
      return;
    }
    if (buffer.length >= batchSize) await drain();
  };

  const flush = (): Promise<void> => {
    chain = chain.then(drain, drain);
    return chain;
  };

  return {
    push(event) {
      buffer.push(event);
      persist();
      if (buffer.length >= batchSize || GRID_VISIBLE.test(event.type)) void flush();
    },
    flush,
    pending: () => buffer.length + inFlight.length,
    beacon() {
      const all = [...inFlight, ...buffer];
      if (all.length && opts.beacon) opts.beacon(all.slice(0, 64));
    },
  };
}

/** The browser's beacon: same origin, so her device cookie rides along. */
export function sendBeacon(events: ClientEvent[]): boolean {
  try {
    const blob = new Blob([JSON.stringify({ events })], { type: "application/json" });
    return navigator.sendBeacon("/api/v1/child/events", blob);
  } catch {
    return false;
  }
}
